import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import ScrollReveal from '../components/ui/ScrollReveal';
import { LUXURY_ICONS } from '../components/home/LuxuryIcons';
import './Home.css';

import heroImage from '../assets/hero_banner.png';

const highlights = [
  {
    id: 'fabric',
    icon: 'fabric',
    title: 'Premium Fabric',
    text: 'Soft-touch materials engineered for everyday luxury.'
  },
  {
    id: 'comfort',
    icon: 'comfort',
    title: 'All-Day Comfort',
    text: 'Designed to move with you from morning to night.'
  },
  {
    id: 'fit',
    icon: 'fit',
    title: 'Perfect Fit',
    text: 'Tailored silhouettes that sit right, every time.'
  },
  {
    id: 'breath',
    icon: 'breath',
    title: 'Breathable Feel',
    text: 'Lightweight construction that keeps you cool and fresh.'
  },
  {
    id: 'confidence',
    icon: 'confidence',
    title: 'Built for Confidence',
    text: 'Essentials that help you show up as your best self.'
  },
  {
    id: 'style',
    icon: 'style',
    title: 'Modern Style',
    text: 'Minimal design language with a refined masculine edge.'
  }
];

const reviews = [
  {
    id: 1,
    name: 'Hardeep Singh',
    location: 'Ludhiana, Punjab',
    rating: 5,
    comment:
      'The fit is unreal. Soft, breathable, and looks premium, finally underwear that feels as good as it looks.'
  },
  {
    id: 2,
    name: 'Taran',
    location: 'Patiala, Punjab',
    rating: 5,
    comment:
      'I wear YANX every day. The fabric quality is on another level. Comfort without compromising style.'
  },
  {
    id: 3,
    name: 'Chetan',
    location: 'Amritsar, Punjab',
    rating: 5,
    comment:
      'Minimal, masculine, and incredibly comfortable. This is what modern essentials should feel like.'
  }
];

const getInitials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const Home = () => {
  const [heroLoaded, setHeroLoaded] = useState(false);
const featuredProducts = products.filter(p => [2, 3, 4].includes(p.id));

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);


  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
  <div className="hero-static" style={{ backgroundImage: `url(${heroImage})` }}>
    <div className="hero-overlay" />
    <div className={`hero-content ${heroLoaded ? 'hero-content--visible' : ''}`}>
     
      <h1 className="hero-title">
        Comfort that moves
        <span className="hero-title-accent"> with confidence.</span>
      </h1>
      <p className="hero-tagline">
        Everyday luxury designed for modern men who value fit, feel, and self-expression.
      </p>
      <div className="hero-buttons">
        <Link to="/products" className="btn btn-primary">
          Shop Collection
        </Link>
        <a href="#our-story" className="btn btn-secondary">
          Our Story
        </a>
      </div>
    </div>
  </div>
</section>

      {/* Our Story */}
      <section className="brand-story" id="our-story" aria-labelledby="story-heading">
        <div className="story-editorial">
          <span className="story-watermark" aria-hidden="true">
            YANX
          </span>

          <div className="container">
            <div className="story-layout">
              <ScrollReveal className="story-header">
                <p className="section-eyebrow section-eyebrow--rule">Our Story</p>
                <h2 id="story-heading" className="story-headline">
                  Confidence starts
                  <span className="story-headline-accent"> underneath.</span>
                </h2>
                <p className="story-deck">
                  Built for comfort. Designed for confidence.
                </p>
              </ScrollReveal>

              <div className="story-pillars">
                <ScrollReveal className="story-pillar" delay={80}>
                  <span className="story-pillar-num">01</span>
                  <h3 className="story-pillar-title">Everyday luxury</h3>
                  <p className="story-pillar-copy">
                    Essentials that feel elevated from the first wear — soft, refined, and
                    unmistakably yours.
                  </p>
                </ScrollReveal>

                <ScrollReveal className="story-pillar" delay={160}>
                  <span className="story-pillar-num">02</span>
                  <h3 className="story-pillar-title">Movement &amp; ease</h3>
                  <p className="story-pillar-copy">
                    Engineered to move with your body through long days, workouts, and
                    everything in between.
                  </p>
                </ScrollReveal>

                <ScrollReveal className="story-pillar" delay={240}>
                  <span className="story-pillar-num">03</span>
                  <h3 className="story-pillar-title">Modern masculinity</h3>
                  <p className="story-pillar-copy">
                    Minimal design language for men who value fit, feel, and quiet confidence.
                  </p>
                </ScrollReveal>
              </div>
            </div>

            <ScrollReveal className="story-banner" delay={320}>
              <div className="story-banner-inner">
                <p className="story-banner-text">
                  YANX was created for men who expect more from what they wear closest to
                  their skin — premium pieces that help you feel good in your own skin,
                  every single day.
                </p>
                <Link to="/products" className="btn btn-story-cta">
                  Explore Essentials
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Choose YANX */}
      <section className="quality-highlights">
        <div className="container">
          <ScrollReveal className="section-header">
            <p className="section-eyebrow">Why YANX</p>
            <h2 className="section-title">Engineered for modern masculinity</h2>
            <p className="section-subtitle">
              Premium essentials designed for comfort, confidence, and everyday performance.
            </p>
          </ScrollReveal>

          <div className="highlights-grid">
            {highlights.map((item, index) => {
              const Icon = LUXURY_ICONS[item.icon];
              return (
                <ScrollReveal key={item.id} className="highlight-card-wrap" delay={index * 80}>
                  <article className="highlight-card">
                    <div className="highlight-icon">{Icon && <Icon />}</div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-products">
        <div className="container">
          <ScrollReveal className="section-header">
            <p className="section-eyebrow">Our Collection</p>
            <h2 className="section-title">Featured Essentials</h2>
          </ScrollReveal>

          <div className="products-grid">
            {featuredProducts.map((product, index) => (
              <ScrollReveal key={product.id} className="product-card-wrap" delay={index * 100}>
                <article className="product-card">
                  <div className="product-image-wrapper">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="product-image"
                      loading="lazy"
                    />
                    <div className="product-overlay">
                    <Link to="/products#top"  className="btn btn-overlay">
                        View Details
                      </Link>
                    </div>
                  </div>
                  <div className="product-info">
                    <span className="product-category">{product.category}</span>
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-price">
                      Rs. {product.price.toLocaleString('en-IN')}
                    </p>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>

          {/* <ScrollReveal className="section-cta" delay={200}>
            <Link to="/products" className="btn btn-primary btn-large">
              View All Products
            </Link>
          </ScrollReveal> */}
        </div>
      </section>

      {/* Testimonials */}
      <section className="reviews">
        <div className="container">
          <ScrollReveal className="section-header">
            <p className="section-eyebrow">Testimonials</p>
            <h2 className="section-title">What our Customers say</h2>
            <p className="section-subtitle">
              Real experiences from men who made YANX part of their everyday.
            </p>
          </ScrollReveal>

          <div className="reviews-grid">
            {reviews.map((review, index) => (
              <ScrollReveal key={review.id} className="review-card-wrap" delay={index * 100}>
                <article className="review-card">
                  <div className="review-rating" aria-label={`${review.rating} out of 5 stars`}>
                    {[...Array(review.rating)].map((_, i) => (
                      <span key={i} className="review-star" />
                    ))}
                  </div>
                  <blockquote className="review-comment">&ldquo;{review.comment}&rdquo;</blockquote>
                  <div className="review-author-row">
                    <span className="review-avatar">{getInitials(review.name)}</span>
                    <div>
                      <p className="review-author">{review.name}</p>
                      <p className="review-location">{review.location}</p>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <div className="cta-backdrop" aria-hidden="true">
          <span className="cta-watermark">YANX</span>
        </div>
        <div className="container">
          <ScrollReveal className="cta-inner">
            <span className="section-eyebrow">The Collection</span>
            <h2 id="cta-heading" className="cta-title">
              Elevate your <em>everyday</em>
            </h2>
            <p className="cta-text">
              Premium men&apos;s essentials designed for comfort, confidence, and modern style.
              Feel the difference from the first wear.
            </p>
            <div className="cta-actions">
              <Link to="/products" className="btn btn-cta-primary">
                Shop Collection
              </Link>
              <Link to="/contact" className="btn btn-cta-secondary">
                Get in Touch
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
